import { type ReactElement, useState } from 'react';
import { useAuthStore, useUsersListQuery } from '@/entities/user';
import { BlockUserForm, useUserModeration } from '@/features/users';
import { cn, EmptyState, Pagination, tableStyles } from '@/shared/ui';
import { UserRow } from './user-row';

interface UsersTableProps {
  readonly page: number;
  readonly search: string;
  readonly onPageChange: (page: number) => void;
}

/** 사용자 목록 표 — 조회·차단·해제·페이지 이동을 묶은 자립 블록. */
export const UsersTable = ({ page, search, onPageChange }: UsersTableProps): ReactElement => {
  const currentUserId = useAuthStore((state) => state.user?.id);
  const usersQuery = useUsersListQuery({ page, search });
  const moderation = useUserModeration();
  const [blockTargetId, setBlockTargetId] = useState<string | null>(null);

  const handleBlockSubmit = async (reason: string): Promise<void> => {
    if (blockTargetId && (await moderation.block(blockTargetId, reason))) {
      setBlockTargetId(null);
    }
  };

  if (usersQuery.isPending) {
    return <EmptyState>불러오는 중…</EmptyState>;
  }

  if (usersQuery.isError) {
    return <EmptyState isError>목록을 불러오지 못했습니다: {usersQuery.error.message}</EmptyState>;
  }

  if (usersQuery.data.items.length === 0) {
    return <EmptyState>조건에 맞는 회원이 없습니다.</EmptyState>;
  }

  // 폼 유지 조건은 blockTargetId 자체다 — 다른 행 조작으로 목록이 리페치되어
  // 대상이 현재 페이지에서 밀려나도, 입력 중인 사유가 유실되면 안 된다.
  const blockTargetEmail = blockTargetId
    ? (usersQuery.data.items.find((user) => user.id === blockTargetId)?.email ?? '')
    : null;

  return (
    <>
      {moderation.error ? (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {moderation.error}
        </p>
      ) : null}

      <div className={tableStyles.card}>
        <table className={tableStyles.table}>
          <thead className={tableStyles.head}>
            <tr>
              <th className={tableStyles.th}>이메일</th>
              <th className={tableStyles.th}>이름</th>
              <th className={tableStyles.th}>역할</th>
              <th className={tableStyles.th}>상태</th>
              <th className={tableStyles.th}>가입일</th>
              <th className={cn(tableStyles.th, 'text-right')}>관리</th>
            </tr>
          </thead>
          <tbody>
            {usersQuery.data.items.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                canModerate={user.role !== 'ADMIN' && user.id !== currentUserId && !user.deletedAt}
                isModerationPending={moderation.pendingUserId === user.id}
                onBlockRequest={setBlockTargetId}
                onUnblock={(userId) => void moderation.unblock(userId)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {blockTargetId !== null && blockTargetEmail !== null ? (
        <BlockUserForm
          email={blockTargetEmail}
          isPending={moderation.pendingUserId === blockTargetId}
          onSubmit={(reason) => void handleBlockSubmit(reason)}
          onCancel={() => setBlockTargetId(null)}
        />
      ) : null}

      <Pagination
        className="mt-5 justify-center"
        page={usersQuery.data.meta.page}
        totalPages={usersQuery.data.meta.totalPages}
        onPageChange={onPageChange}
      />
    </>
  );
};
